import { Controller, FieldPath, useFieldArray, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Anchor, OnOffSwitch } from '@any-ui-react/core';

import { AnyChatForm, isValidCrmUrl, removeTrackingLinkNodes } from '@anyx/feature-crm/shared';
import { AnyXLanguage, DocUtils } from '@anyx/shared-utils';

import { useLineMessageStore } from '../store';
import { LineMessageBlock, LineMessageFormFieldsProps } from '../types';
import { getManualActionURL } from '../utils';

export const TrackingSwitch = () => {
  const { t } = useTranslation('crm');
  const { control } = useFormContext<LineMessageFormFieldsProps>();
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);
  const { disableTracking } = useToggleTrackingSwitch();

  return (
    <AnyChatForm.Block blockTitle={t('crm.lineMessageForm.fields.trackingEnabled.label')}>
      <Trans
        t={t}
        ns="crm"
        parent="p"
        className="text-secondary text-3xs -mt-2"
        i18nKey="crm.lineMessageForm.fields.trackingEnabled.description"
        components={[
          <Anchor
            href={DocUtils.getGitbookLink('/crm-customer-management/line-broadcasts', {
              [AnyXLanguage.JA_JP]: '/crm/line/line-broadcasts',
            })}
            target="_blank"
          />,
        ]}
      />
      <Controller
        control={control}
        name="trackingEnabled"
        render={({ field: { value, onChange, ...field } }) => (
          <OnOffSwitch
            {...field}
            disabled={isFormDisabled}
            checked={value}
            onChange={(e) => {
              if (!e.target.checked) {
                disableTracking();
              }
              onChange(e.target.checked);
            }}
          />
        )}
      />
    </AnyChatForm.Block>
  );
};

type FlattenUrlResult = { alias: FieldPath<LineMessageFormFieldsProps>; url: string };

const flattenUrls = (messages: LineMessageBlock[]): FlattenUrlResult[] => {
  const result: FlattenUrlResult[] = [];

  const checkAndPush = (alias: FieldPath<LineMessageFormFieldsProps>, url?: string) => {
    if (url && isValidCrmUrl(url)) {
      result.push({ alias, url });
    }
  };

  messages.forEach((message, i) => {
    if (message.type === 'RICH') {
      const url = message.actionUrl;
      checkAndPush(`messages.${i}.actionUrl.manual` as const, getManualActionURL(url));
    }
    if (message.type === 'IMAGES') {
      message.cards.forEach((card, j) => {
        const url = card.actionUrl;
        checkAndPush(`messages.${i}.cards.${j}.actionUrl.manual` as const, getManualActionURL(url));
      });
    }
    if (message.type === 'BUTTON') {
      message.cards.forEach((card, j) => {
        card.actions.forEach((action, k) => {
          const url = action.url;
          checkAndPush(`messages.${i}.cards.${j}.actions.${k}.url` as const, url);
        });
      });
    }
  });

  return result;
};

function useToggleTrackingSwitch() {
  const { control, setValue, getValues } = useFormContext<LineMessageFormFieldsProps>();

  const { remove: removeTrackingUrls } = useFieldArray({
    control,
    name: 'trackingUrls',
  });

  const disableTracking = () => {
    // 1. Find all messages url
    const urls = flattenUrls(getValues('messages'));
    // 2. Revert messages trackingUrl to originalUrl
    urls.forEach(({ alias, url }) => {
      const originalUrl = getValues('trackingUrls')?.find(
        (trackingUrl) => trackingUrl.trackingUrl === url
      )?.originalUrl;
      if (originalUrl) {
        setValue(alias, originalUrl);
      }
    });
    // 3. Handle plain text
    const messages = getValues('messages');
    messages.forEach((message, i) => {
      if (message.type === 'PLAIN_TEXT' && message.richText) {
        const { richText, text } = removeTrackingLinkNodes(message.richText);
        setValue(`messages.${i}.richText`, richText);
        setValue(`messages.${i}.text`, text);
      }
    });
    // 4. Remove trackingUrls
    removeTrackingUrls();
  };

  return {
    disableTracking,
  };
}
